import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { getBackendUrl } from "../config/api";

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription({
        plan_id: "free",
        plan_name: "Free",
        is_free: true,
        features: getDefaultFeatures("free")
      });
      setLoading(false);
      return;
    }

    try {
      const API_URL = getBackendUrl(); // Get URL at runtime
      const response = await fetch(`${API_URL}/api/subscriptions/user/${user.id}`);
      const data = await response.json();
      setSubscription(data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
      setSubscription({
        plan_id: "free",
        plan_name: "Free",
        is_free: true,
        features: getDefaultFeatures("free")
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const checkFeature = useCallback((featureName) => {
    if (!subscription?.features) return false;
    const value = subscription.features[featureName];
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (Array.isArray(value)) return value.length > 0;
    return value !== null && value !== undefined;
  }, [subscription]);

  const getFeatureLimit = useCallback((featureName) => {
    if (!subscription?.features) return null;
    return subscription.features[featureName];
  }, [subscription]);

  const isPro = subscription?.plan_id === "pro" || subscription?.plan_id === "studio";
  const isStudio = subscription?.plan_id === "studio";

  const value = {
    subscription,
    loading,
    checkFeature,
    getFeatureLimit,
    refreshSubscription: fetchSubscription,
    isPro,
    isStudio,
    planId: subscription?.plan_id || "free",
    planName: subscription?.plan_name || "Free"
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
}

// Default features for when API fails
function getDefaultFeatures(planId) {
  const plans = {
    free: {
      max_tracks: 4,
      max_projects: 3,
      storage_gb: 2,
      export_formats: ["wav"],
      amp_models: ["clean", "crunch", "acoustic", "vintageDeluxe", "jazzClean"],
      pedal_effects: ["distortion", "overdrive", "chorus", "compressor", "wah"],
      preset_downloads_per_month: 0,
      can_sell_marketplace: false,
      midi_tools: false,
      automation_lanes: false,
      piano_roll: false,
      plugin_system: false,
      undo_history: 20
    },
    pro: {
      max_tracks: 8,
      max_projects: -1,
      storage_gb: 10,
      export_formats: ["wav", "mp3"],
      amp_models: ["clean", "crunch", "acoustic", "vintageDeluxe", "jazzClean", "highGain", "metal", "britishStack", "bassDriver", "bluesBreaker"],
      pedal_effects: ["distortion", "overdrive", "chorus", "compressor", "wah", "fuzz", "booster", "autoWah", "phaser", "flanger", "tremolo", "vibrato", "delay", "reverb", "noisegate"],
      preset_downloads_per_month: 10,
      can_sell_marketplace: false,
      midi_tools: true,
      automation_lanes: false,
      piano_roll: false,
      plugin_system: false,
      undo_history: 50
    },
    studio: {
      max_tracks: -1,
      max_projects: -1,
      storage_gb: 50,
      export_formats: ["wav", "mp3"],
      amp_models: ["clean", "crunch", "acoustic", "vintageDeluxe", "jazzClean", "highGain", "metal", "britishStack", "bassDriver", "bluesBreaker", "modernMetal", "matchless", "dualRecto"],
      pedal_effects: ["distortion", "overdrive", "chorus", "compressor", "wah", "fuzz", "booster", "autoWah", "phaser", "flanger", "tremolo", "vibrato", "delay", "reverb", "noisegate", "octaver", "pitchShifter", "rotary", "looper", "eqPedal"],
      preset_downloads_per_month: -1,
      can_sell_marketplace: true,
      midi_tools: true,
      automation_lanes: true,
      piano_roll: true,
      plugin_system: true,
      undo_history: 100
    }
  };
  return plans[planId] || plans.free;
}

export default SubscriptionContext;
